/* Common Imports*/

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	UpdateAboutPropertyThunk,
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Snackbar,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Import */

import { Add, Close, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { confirmSendForReview, skipNextSendForReviewConfirm } from "../../SendForReviewConfirm";
import { confirmDiscardChanges, useEditSnapshot } from "../../SectionDiscardConfirm";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/* Styling */

const Container = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "400",
	color: theme.palette.text.primary,
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const ULList = styled("ul")(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	paddingLeft: "1.5rem",
	wordBreak: "break-word",
	width: "100%",
}));

const ListItem = styled("li")(({ theme }) => ({
	color: theme.palette.mode == "dark" ? "#ffffff" : "#666666",
	fontWeight: 400,
	fontSize: "1rem",
	lineHeight: "1.3rem",
	letterSpacing: "0.009rem",
	margin: "0rem 0rem 0.5rem 0rem",
}));

const ShowMore = styled(Typography)(({ theme }) => ({
	margin: "0.25rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	color: theme.palette.primary.main,
	textAlign: "center",
	fontSize: "0.85rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		margin: "0.5rem 0rem 0rem 0rem",
	},
}));

const EditContainer = styled("div")(({ theme }) => ({
	width: "100%",
}));

const InputRemove = styled("div")(({ theme }) => ({
	width: "100%",
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "1rem",
	margin: "0rem 0rem 0.5rem 0rem",
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const AboutPropertyCard = ({
	session,
	property_id,
	property_created_by,
	about_property,
	dispatch,
	negativeScroll,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	assigned_to,
	about_property_snackbar,
	screenType,
}: {
	session: any;
	property_id: string | ObjectId;
	property_created_by: LocalPropertyInterface["created_by"];
	about_property: string[];
	dispatch: Function;
	negativeScroll: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	assigned_to: StateInterface["single_property_page"]["property"]["assigned_to"];
	about_property_snackbar: boolean;
	screenType: string;
}) => {
	const theme = useTheme();

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router = React.useRef(useRouter()).current;

	const localLang = {
		snackValue:
			router.locale === "kn-IN"
				? "ನವೀಕರಣ ಯಶಸ್ವಿಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث معلومات العقار بنجاح"
					: "About property updated successfully",
		fieldsNOEmpty:
			router.locale === "kn-IN"
				? "ಕ್ಷೇತ್ರಗಳು ಖಾಲಿ ಇರಲು ಸಾಧ್ಯವಿಲ್ಲ!"
				: router.locale === "ar-AE"
					? "لا يمكن أن تكون الحقول فارغة!"
					: "Fields cannot be empty!",
		aboutProperty:
			router.locale === "kn-IN" ? "ಪ್ರಾಪರ್ಟಿ ಬಗ್ಗೆ" : router.locale === "ar-AE" ? "عن العقار" : "About property",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		addMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "أضف المزيد" : "Add more",
		seeMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية المزيد" : "See more",
		seeLess: router.locale === "kn-IN" ? "ಕಡಿಮೆ ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية أقل" : "See less",
	};

	const [showEmptyFielSnack, setShowEmptyFieldSnack] = React.useState(false);
	const [load, setLoad] = React.useState<Boolean>(false);
	const [showMore, setShowMore] = React.useState<Boolean>(false);

	const [isEditing, setIsEditing] = React.useState<Boolean>(false);
	const isSectionDirty = useEditSnapshot(Boolean(isEditing));

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	React.useEffect(() => {
		if (section_to_edit !== "about-property") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	if (loading) {
		return (
			<Container
				id="about-property"
				sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
			>
				<Heading>
					<Skeleton
						animation="wave"
						variant="text"
						width="15rem"
					/>
				</Heading>
				<Contain sx={{ justifyContent: "flex-start", gap: "1rem" }}>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="50%"
					/>
				</Contain>
				<Contain sx={{ justifyContent: "flex-start", gap: "1rem" }}>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="50%"
					/>
				</Contain>
				<Contain sx={{ justifyContent: "flex-start", gap: "1rem" }}>
					<Skeleton
						animation="wave"
						variant="text"
						width="1rem"
					/>
					<Skeleton
						animation="wave"
						variant="text"
						width="50%"
					/>
				</Contain>
			</Container>
		);
	}

	const handleAboutAdd = () => {
		dispatch(setSinglePropertyAboutThunk([...about_property, ""]));
	};

	const handleAboutRemove = (index: number) => {
		const list = [...about_property];
		list.splice(index, 1);
		dispatch(setSinglePropertyAboutThunk(list));
	};

	const handleAboutChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
		const { value } = event.target;
		const list = [...about_property];
		list[index] = value;
		dispatch(setSinglePropertyAboutThunk(list));
	};

	const aboutPropertyCardContainer = document.getElementById("about-property");

	const submitForm = async () => {
		/* Saving unlists the listing until it is approved again: say so first (gap A3, the app's prompt). */
		if (!(await confirmSendForReview())) return;
		setLoad(true);
		const data = about_property.filter((item) => item !== "");
		if (data.length === about_property.length) {
			await dispatch(
				UpdateAboutPropertyThunk({
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
					about_property: about_property,
					property_id: property_id,
					phoneNumber: session ? session.user.dbUser.phone : "",
					countryCode: session ? session.user.dbUser.country_code : "",
					editor_is_business_profile: isEditor
						? profile_context.is_business_profile
						: property_created_by.is_business_profile,
					editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
					editor_business_profile_id: isEditor
						? profile_context.business_profile_id
						: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
					url: router.asPath ? router.asPath : "",
					profile_context: profile_context,
				}),
			);
			dispatch(setSectionToEditThunk(""));
			dispatch(setDataBeforeEditThunk({}));
			setIsEditing(false);
			aboutPropertyCardContainer
				? aboutPropertyCardContainer.scrollIntoView({
						behavior: "smooth",
						block: "center",
						inline: "start",
					})
				: null;
		} else {
			setShowEmptyFieldSnack(true);
		}
		setLoad(false);
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== ""
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	return (
		<Container
			id={screenType + "_spp_about_property"}
			sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
		>
			<Snack
				open={about_property_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.5rem" }}
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			<Snack
				open={showEmptyFielSnack}
				autoHideDuration={3000}
				onClose={() => setShowEmptyFieldSnack(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setShowEmptyFieldSnack(false)}
					severity="error"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.fieldsNOEmpty}
				</Alert>
			</Snack>

			{/* Icon */}

			<Contain>
				<Heading>{localLang.aboutProperty}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id={screenType + "_spp_about_property_edit_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("about-property"));
									if (section_to_edit !== "about-property") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("about-property"));
									}
									setIsEditing(true);
									dispatch(setDataBeforeEditThunk(about_property));
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id={screenType + "_spp_about_property_close_button"}																																		
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={async () => {
									/* Unsaved changes ask first (3 Nov 2026, the app's "Discard changes?"). */
									if (isSectionDirty()) {
										const answer = await confirmDiscardChanges();
										if (answer === "keep") return;
										if (answer === "save") {
											skipNextSendForReviewConfirm();
											await submitForm();
											return;
										}
									}
									setIsEditing(false);
									dispatch(setSinglePropertyAboutThunk(data_before_edit));
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{!isEditing ? (
				/* User View */

				<ULList>
					{showMore
						? about_property.map((item: string) =>
								item === "" ? null : (
									<ListItem key={item}>
										<SubHeading variant="body1">{item}</SubHeading>
									</ListItem>
								),
							)
						: about_property.slice(0, 3).map((item: string) =>
								item === "" ? null : (
									<ListItem key={item}>
										<SubHeading variant="body1">{item}</SubHeading>
									</ListItem>
								),
							)}

					{about_property.length > 3 && (
						<ShowMore
							id={screenType + "_spp_about_property_show_more"}
							onClick={() => {
								setShowMore(!showMore);
							}}
							sx={{ cursor: "pointer" }}
						>
							{showMore ? localLang.seeLess : localLang.seeMore}
						</ShowMore>
					)}
				</ULList>
			) : (
				/* Edit View */

				<EditContainer>
					{about_property.length > 0 ? (
						about_property.map((aboutprop: string, index: number) => (
							<div key={index}>
								<InputRemove>
									<TextField
										id={screenType + "_spp_about_property_edit_textfield"}
										name="about property"
										size="small"
										label={localLang.aboutProperty}
										fullWidth
										margin="dense"
										type="text"
										multiline
										maxRows={10}
										sx={{
											width: "100%",
											"& .MuiInputBase-input": {
												fontSize: "0.825rem",
												[theme.breakpoints.up("sm")]: {
													fontSize: "0.875rem",
												},
												[theme.breakpoints.up("md1190")]: {
													fontSize: "1rem",
												},
											},
										}}
										variant="outlined"
										value={aboutprop}
										placeholder="Eg: RERA Registered, BBMP Approved, IGBC Certified, Vaastu Compliance etc."
										onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
											if (section_to_edit !== "about-property") {
												dispatch(setSectionToEditThunk("about-property"));
											}
											handleAboutChange(event, index);
										}}
										autoComplete="off"
									/>

									{about_property.length >= 1 && (
										<IconButton
											id={screenType + "_spp_about_property_remove_button"}
											color="primary"
											onClick={() => {
												if (section_to_edit !== "about-property") {
													dispatch(setSectionToEditThunk("about-property"));
												}
												handleAboutRemove(index);
											}}
										>
											<DeleteOutline />
										</IconButton>
									)}
								</InputRemove>

								{about_property.length - 1 === index && about_property.length < 50 && (
									<IconButton
										id={screenType + "_spp_about_property_add_button"}
										color="primary"
										onClick={() => {
											if (section_to_edit !== "about-property") {
												dispatch(setSectionToEditThunk("about-property"));
											}
											handleAboutAdd();
										}}
										sx={{
											fontSize: "0.875rem",
										}}
									>
										<Add />
										{localLang.addMore}
									</IconButton>
								)}
							</div>
						))
					) : (
						<React.Fragment>
							<IconButton
								id={screenType + "_spp_about_property_add_button"}
								color="primary"
								sx={{ margin: "1rem 0rem 1rem 0 rem", fontSize: "0.875rem" }}
								onClick={() => {
									if (section_to_edit !== "about-property") {
										dispatch(setSectionToEditThunk("about-property"));
									}
									handleAboutAdd();
								}}
							>
								<Add />
								{localLang.addMore}
							</IconButton>
							<br />
						</React.Fragment>
					)}

					{/* Update Button */}

					{load ? (
						<CircularProgress
							// size={24}
							sx={{
								marginTop: "1rem",
							}}
						/>
					) : (
						<BeegruButton
							id={screenType + "_spp_about_property_update_button"}
							flavor="primary"
							onClick={submitForm}
							sx={{ marginTop: "0.5rem", width: "100%" }}
							variant="contained"
							type="button"
							name="Update"
						>
							{localLang.update}
						</BeegruButton>
					)}
				</EditContainer>
			)}
		</Container>
	);
};

export default AboutPropertyCard;
